import { Calendar } from "components/ui/calendar";
import { Label } from "components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { format, isBefore, isAfter, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { useEffect, useState } from "react";

interface SingleDatePickerProps {
  id?: string;
  date?: Date;
  onDateChange?: (date: Date | undefined) => void;
  label?: string;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
}

const isDateDisabled = (
  checkDate: Date,
  minDate?: Date,
  maxDate?: Date
): boolean => {
  const dateToCheck = startOfDay(checkDate);
  const today = startOfDay(new Date());

  if (isAfter(dateToCheck, today)) return true;
  if (minDate && isBefore(dateToCheck, startOfDay(minDate))) return true;
  if (maxDate && isAfter(dateToCheck, startOfDay(maxDate))) return true;

  return false;
};

function SingleDatePicker({
  id,
  date: initialDate,
  onDateChange,
  label,
  className,
  minDate,
  maxDate,
}: SingleDatePickerProps) {
  const [date, setDate] = useState<Date | undefined>(initialDate);
  const [isOpen, setIsOpen] = useState(false);
  const [month, setMonth] = useState(initialDate || new Date());

  useEffect(() => {
    setDate(initialDate);
    setMonth(initialDate || new Date());
  }, [initialDate]);

  const handleSelect = (selectedDate: Date | undefined) => {
    setDate(selectedDate);
    setMonth(selectedDate || new Date());
    if (onDateChange) {
      onDateChange(selectedDate);
    }
    setIsOpen(false);
  };

  return (
    <div className={`flex flex-col gap-2 ${className || ""}`}>
      {label && (
        <Label htmlFor={id} className="block text-sm font-medium text-gray-700">
          {label}
        </Label>
      )}
      <Popover open={isOpen} onOpenChange={setIsOpen} modal={true}>
        <PopoverTrigger asChild>
          <button
            id={id}
            className="flex items-center w-full h-8 px-3 py-1 border rounded-md text-left text-sm text-gray-700 shadow-sm"
            onClick={() => setIsOpen(!isOpen)}
          >
            <span className="flex-1">
              {date
                ? format(date, "dd/MM/yyyy", { locale: ptBR })
                : "Selecione uma data"}
            </span>
            <CalendarIcon className="text-gray-500  ml-2" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="single"
            selected={date}
            onSelect={handleSelect}
            month={month}
            onMonthChange={setMonth}
            locale={ptBR}
            fromYear={2000}
            disabled={(checkDate: Date) => isDateDisabled(checkDate, minDate, maxDate)}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default SingleDatePicker;
